<script setup lang="ts">
import { Check, LucidePencil } from 'lucide-vue-next'
import { Button } from '~/components/ui/button'
import { EntityOrganizationLogo } from '~/components/entity/organization-logo'
import type { IRatingTarget } from './types'

const props = defineProps<{
  item: IRatingTarget
  selected?: boolean
  /** Renders as a summary of an answered step — offers editing instead of selecting */
  readonly?: boolean
  /** Label for the edit action; required when `readonly` is set */
  editLabel?: string
  /** Labels for the select action, in its two states */
  selectLabel?: string
  selectedLabel?: string
}>()

const emit = defineEmits<{
  select: [item: IRatingTarget]
  edit: []
}>()
</script>

<template>
  <article
    class="Card flex items-center gap-3"
    :class="{ 'ring-2 ring-blue-600': props.selected, 'hover:shadow-none': props.readonly }"
  >
    <EntityOrganizationLogo
      :src="props.item.logo"
      :alt="props.item.title"
      :to="props.item.url"
    />

    <div class="flex min-w-0 flex-col">
      <NuxtLinkLocale
        v-if="props.item.url"
        :to="props.item.url"
        class="truncate font-semibold text-gray-900 hover:underline"
      >
        {{ props.item.title }}
      </NuxtLinkLocale>
      <span v-else class="truncate font-semibold text-gray-900">{{ props.item.title }}</span>

      <NuxtLinkLocale
        v-if="props.item.organization"
        :to="props.item.organization.url"
        class="CardOrgLink truncate"
      >
        {{ props.item.organization.title }}
      </NuxtLinkLocale>
    </div>

    <Button
      v-if="props.readonly"
      type="button"
      variant="ghost"
      size="sm"
      class="ml-auto shrink-0 text-blue-600 hover:bg-blue-50"
      :title="props.editLabel"
      :aria-label="props.editLabel"
      @click="emit('edit')"
    >
      <LucidePencil class="h-4 w-4" />
    </Button>

    <Button
      v-else
      type="button"
      class="ml-auto shrink-0"
      :variant="props.selected ? 'blue' : 'outline-blue'"
      size="sm"
      @click="emit('select', props.item)"
    >
      <Check v-if="props.selected" class="h-4 w-4" />
      {{ props.selected ? props.selectedLabel : props.selectLabel }}
    </Button>
  </article>
</template>
